import type { ColorTheme } from 'lib-trend-core';
import { ChatAppTarget, buildChatAppPath, isSafeChatAppPath } from './chat-app-url.util';
import { ChatShellTarget } from './chat-shell.service';

/**
 * The product's side of "open this in the chat app" — SPEC-2026-09-internal-chat-pwa
 * (T-40 · RF-05 · RF-11 · RF-12). Pure helpers, so the launcher's decisions are
 * testable without windows, timers or HTTP.
 */

/** Name of the chat app window: maximizing again reuses and focuses the same tab. */
export const CHAT_APP_WINDOW_NAME = 'trendmulti-chat';

/** Posted by the product to an already-open chat app window. */
export const CHAT_APP_OPEN_MESSAGE = 'TRENDMULTI_CHAT_OPEN';

/** Posted back by the chat app, so the product stops retrying. */
export const CHAT_APP_OPEN_ACK = 'TRENDMULTI_CHAT_OPEN_ACK';

/** Where the bubble was when the user asked for the chat app. */
export interface ChatBubbleLaunchState {
  channelId?: string | null;
  directUserId?: string | null;
  messageId?: string | null;
}

/**
 * The destination to hand over: an explicit target (a pane, a search, a notification)
 * wins; otherwise the conversation the compact window is showing; otherwise the list.
 */
export function resolveChatAppLaunchTarget(state: ChatBubbleLaunchState | null, explicit?: ChatShellTarget | null): ChatAppTarget {
  if (explicit && (explicit.channelId || explicit.directUserId || explicit.view)) {
    return {
      channelId: explicit.channelId || undefined,
      messageId: explicit.messageId || undefined,
      threadRootId: explicit.threadRootId || undefined,
      directUserId: explicit.directUserId || undefined,
      view: explicit.view,
      searchTerm: explicit.searchTerm || undefined,
    };
  }
  if (state?.channelId) {
    return { channelId: state.channelId, messageId: state.messageId || undefined };
  }
  if (state?.directUserId) {
    return { directUserId: state.directUserId };
  }
  return {};
}

/** Origin of the chat app, or null when the configured URL is not an http(s) URL. */
export function chatAppOrigin(chatAppUrl: string | null | undefined): string | null {
  try {
    const url = new URL(String(chatAppUrl ?? ''));
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : null;
  } catch {
    return null;
  }
}

/** Direct entry — the chat app asks for sign-in itself when it has no session. */
export function buildChatAppEntryUrl(chatAppUrl: string, target: ChatAppTarget): string | null {
  const origin = chatAppOrigin(chatAppUrl);
  return origin ? `${origin}${buildChatAppPath(target)}` : null;
}

/**
 * Handoff landing with the one-time code in the FRAGMENT (never sent to a server, never
 * in a `Referer`) and the destination next to it — plus the product's theme, so the chat
 * app opens in the same one (DEV-2062).
 */
export function buildChatAppHandoffUrl(chatAppUrl: string, target: ChatAppTarget, code: string, theme?: ColorTheme): string | null {
  const origin = chatAppOrigin(chatAppUrl);
  if (!origin || !code) return null;
  const path = buildChatAppPath(target);
  const fragment = new URLSearchParams({ code, to: isSafeChatAppPath(path) ? path : '/' });
  if (theme) fragment.set('theme', theme);
  return `${origin}/handoff#${fragment.toString()}`;
}
